/**
 * Sentry Helper Utilities
 *
 * Provides reusable functions for:
 * - Tracking critical operations with spans
 * - Managing user context
 * - Adding flow breadcrumbs
 * - Capturing business-specific errors
 */
import * as Sentry from "@sentry/react";

/**
 * Operation categories for tracking critical flows in the application.
 */
export type OperationCategory =
  | "folder"
  | "vault"
  | "requirement"
  | "document"
  | "user"
  | "auth"
  | "template"
  | "signature";

interface TrackOperationOptions {
  /** Additional tags to attach to the span */
  tags?: Record<string, string>;
  /** Whether this is a critical operation (affects error level) */
  critical?: boolean;
}

/**
 * Tracks a critical operation with Sentry performance monitoring.
 * Creates a span for the operation and captures any errors with enhanced context.
 *
 * @example
 * ```ts
 * // Track document upload
 * const result = await trackCriticalOperation(
 *   'upload_document',
 *   'document',
 *   async () => {
 *     return await uploadDocument(file, requirementId);
 *   },
 *   { critical: true, tags: { requirement_type: 'identification' } }
 * );
 *
 * // Track folder creation
 * await trackCriticalOperation(
 *   'create_folder',
 *   'folder',
 *   async () => createFolder(data)
 * );
 * ```
 */
export async function trackCriticalOperation<T>(
  operationName: string,
  category: OperationCategory,
  fn: () => Promise<T>,
  options: TrackOperationOptions = {}
): Promise<T> {
  const { tags = {}, critical = false } = options;

  return Sentry.startSpan(
    {
      name: operationName,
      op: category,
      attributes: {
        "operation.critical": critical,
        "operation.category": category,
        ...tags,
      },
    },
    async (span) => {
      try {
        const result = await fn();

        // Mark span as successful
        span.setStatus({ code: 1, message: "ok" });

        // Add measurement for successful operations
        Sentry.setMeasurement(`${category}.success`, 1, "none");

        return result;
      } catch (error) {
        // Mark span as failed
        span.setStatus({ code: 2, message: "error" });

        // Capture exception with enhanced context
        Sentry.withScope((scope) => {
          scope.setTag("operation", operationName);
          scope.setTag("category", category);
          scope.setTag("critical", String(critical));

          // Add custom tags
          Object.entries(tags).forEach(([key, value]) => {
            scope.setTag(key, value);
          });

          // Set error level based on criticality
          scope.setLevel(critical ? "fatal" : "error");

          Sentry.captureException(error);
        });

        throw error;
      }
    }
  );
}

/**
 * Adds a custom breadcrumb for tracking user actions in critical flows.
 *
 * @example
 * ```ts
 * // Track folder navigation
 * addFlowBreadcrumb('folder', 'User opened folder details', { folderId: '123' });
 *
 * // Track document upload start
 * addFlowBreadcrumb('document', 'User started document upload', {
 *   requirementId: 'req_456',
 *   documentType: 'identification'
 * });
 *
 * // Track vault access
 * addFlowBreadcrumb('vault', 'Tenant accessed vault', { vaultId: 'v_789' });
 * ```
 */
export function addFlowBreadcrumb(
  category: OperationCategory,
  message: string,
  data?: Record<string, string | number | boolean>
): void {
  Sentry.addBreadcrumb({
    category: `flow.${category}`,
    message,
    level: "info",
    data,
  });
}

/**
 * User context data for Sentry identification.
 */
interface UserContextData {
  id?: string | number;
  email?: string;
  username?: string;
  /** User's group/role (e.g., 'admin', 'user') */
  role?: string;
  /** Whether the user account is validated */
  isValidated?: boolean;
  /** Number of folders the user has */
  folderCount?: number;
}

/**
 * Sets user context with additional business-specific tags.
 * Call this after user data is loaded (e.g., after onGetUserInfo succeeds).
 *
 * Note: Email is intentionally omitted from Sentry.setUser() for privacy,
 * but you can include it if your privacy policy allows.
 *
 * @example
 * ```ts
 * // In authSlice after getUserInfo succeeds
 * setUserContext({
 *   id: user.id,
 *   username: user.name,
 *   role: user.groups?.[0],
 *   isValidated: user.is_validated,
 *   folderCount: user.folder_count,
 * });
 * ```
 */
export function setUserContext(userData: UserContextData): void {
  // Set Sentry user (omit email for privacy unless explicitly needed)
  Sentry.setUser({
    id: userData.id?.toString(),
    username: userData.username,
  });

  // Set business context tags for filtering in Sentry dashboard
  if (userData.role) {
    Sentry.setTag("user.role", userData.role);
  }

  if (userData.isValidated !== undefined) {
    Sentry.setTag("user.validated", String(userData.isValidated));
  }

  // Set additional context
  Sentry.setContext("user_profile", {
    role: userData.role,
    is_validated: userData.isValidated,
    folder_count: userData.folderCount,
  });
}

/**
 * Clears all user context and business tags.
 * Call this on logout to ensure no cross-user contamination of Sentry data.
 *
 * @example
 * ```ts
 * // In logout action or reducer
 * clearUserContext();
 * ```
 */
export function clearUserContext(): void {
  Sentry.setUser(null);

  // Clear business context tags
  Sentry.setTag("user.role", undefined);
  Sentry.setTag("user.validated", undefined);

  // Clear additional context
  Sentry.setContext("user_profile", null);
}

/**
 * Captures a business error with structured context.
 * Use this for domain-specific errors that need extra context.
 *
 * @example
 * ```ts
 * // Document validation failed
 * captureBusinessError(
 *   new Error('Document validation failed'),
 *   'document',
 *   {
 *     requirementId: 'req_123',
 *     validationReason: 'blurry_image',
 *     attemptNumber: 3,
 *   }
 * );
 *
 * // Folder creation failed
 * captureBusinessError(
 *   error,
 *   'folder',
 *   { templateId: 'tmpl_456', step: 'validation' }
 * );
 * ```
 */
export function captureBusinessError(
  error: Error | unknown,
  category: OperationCategory,
  context: Record<string, string | number | boolean>
): void {
  Sentry.withScope((scope) => {
    scope.setTag("error_type", "business");
    scope.setTag("category", category);

    scope.setContext("business_context", context);

    Sentry.captureException(error);
  });
}

/**
 * Sets the current vault context for error tracking.
 * Call this when a user enters a vault to track errors within that context.
 *
 * @example
 * ```ts
 * // When entering a vault
 * setVaultContext({
 *   vaultId: vault.id,
 *   folderId: vault.folder_id,
 *   participantType: 'tenant',
 * });
 *
 * // When leaving the vault
 * clearVaultContext();
 * ```
 */
export function setVaultContext(context: {
  vaultId: string | number;
  folderId?: string | number;
  participantType?: string;
}): void {
  Sentry.setContext("vault", {
    vault_id: context.vaultId,
    folder_id: context.folderId,
    participant_type: context.participantType,
  });

  Sentry.setTag("vault_id", context.vaultId.toString());
}

/**
 * Clears the vault context when leaving a vault.
 */
export function clearVaultContext(): void {
  Sentry.setContext("vault", null);
  Sentry.setTag("vault_id", undefined);
}

/**
 * Sets the current folder context for error tracking.
 * Call this when a user is working within a specific folder.
 *
 * @example
 * ```ts
 * setFolderContext({
 *   folderId: folder.id,
 *   folderStatus: folder.status,
 *   templateId: folder.template_id,
 * });
 * ```
 */
export function setFolderContext(context: {
  folderId: string | number;
  folderStatus?: string;
  templateId?: string | number;
}): void {
  Sentry.setContext("folder", {
    folder_id: context.folderId,
    folder_status: context.folderStatus,
    template_id: context.templateId,
  });

  Sentry.setTag("folder_id", context.folderId.toString());
}

/**
 * Clears the folder context.
 */
export function clearFolderContext(): void {
  Sentry.setContext("folder", null);
  Sentry.setTag("folder_id", undefined);
}
